import { ArrowLeft } from 'lucide-react'
import { useEffect, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { CodeBlock } from '@/components/audit/RunDetail'
import { FilterBar } from '@/components/data/FilterBar'
import { LoadingRows } from '@/components/data/SearchInput'
import { Page } from '@/components/layout/Page'
import { api } from '@/lib/api'
import { fmtWhen } from '@/lib/audit'
import { diffFindings } from '@/lib/diffFindings'

type RunDetail = {
  run_id: string
  operation: string
  started_at: string
  findings: unknown[] | null
}

function Column({ title, run, items, tone, empty }: {
  title: string
  run: RunDetail
  items: unknown[]
  tone: string
  empty: string
}) {
  const id = `compare-${title.toLowerCase()}`
  return (
    <section aria-labelledby={id} className="flex min-h-0 min-w-0 flex-1 flex-col border-r border-line last:border-r-0">
      <header className="flex shrink-0 items-baseline gap-2 border-b border-line px-4 py-2">
        <h2 id={id} className="text-[13px] font-semibold text-foreground">
          {title} <span className={`font-mono ${tone}`}>{items.length}</span>
        </h2>
        <span className="truncate font-mono text-[11px] text-text-3" title={run.run_id}>
          {run.run_id} · {fmtWhen(run.started_at)}
        </span>
      </header>
      <div className="min-h-0 flex-1 overflow-auto px-4 py-3">
        {items.length === 0
          ? <p className="text-[13px] text-text-2">{empty}</p>
          : (
            <ul className="space-y-2">
              {items.map((x, i) => (
                <li key={i}><CodeBlock tone={tone}>{JSON.stringify(x, null, 2)}</CodeBlock></li>
              ))}
            </ul>
          )}
      </div>
    </section>
  )
}

export default function AuditCompare() {
  const [params] = useSearchParams()
  const a = params.get('a')
  const b = params.get('b')
  const [da, setDa] = useState<RunDetail | null>(null)
  const [db, setDb] = useState<RunDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!a || !b) {
      setLoading(false)
      setFailed(true)
      return
    }
    setLoading(true)
    Promise.all([api<RunDetail>(`/audit/${a}`), api<RunDetail>(`/audit/${b}`)])
      .then(([ra, rb]) => {
        setDa(ra)
        setDb(rb)
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false))
  }, [a, b])

  const back = (
    <Link to="/audit" className="inline-flex items-center gap-1 text-[12px] text-text-2 hover:text-foreground">
      <ArrowLeft aria-hidden className="size-3.5" /> Audit log
    </Link>
  )
  const notice = (text: ReactNode) => <p className="px-4 py-4 text-[13px] text-text-2">{text}</p>

  let body: ReactNode
  if (loading) body = <LoadingRows label="Loading runs" />
  else if (failed || !da || !db) body = notice('Run not found.')
  else if (da.operation !== db.operation) {
    body = notice(<>Different operations (<span className="font-mono">{da.operation}</span> vs{' '}
      <span className="font-mono">{db.operation}</span>) — not comparable.</>)
  } else if (!da.findings?.length || !db.findings?.length) {
    body = notice('No findings to compare — one or both runs returned none.')
  } else {
    const diff = diffFindings(da.findings, db.findings)
    body = (
      <>
        <FilterBar>
          <span className="font-mono text-[12px] text-foreground">{da.operation}</span>
          <span className="text-ok">{diff.added.length} added</span>
          <span className="text-danger">{diff.removed.length} removed</span>
          <span className="text-text-2">{diff.common} unchanged</span>
        </FilterBar>
        <div className="flex min-h-0 flex-1">
          <Column title="Before" run={da} items={diff.removed} tone="text-danger" empty="Nothing only in the earlier run." />
          <Column title="After" run={db} items={diff.added} tone="text-ok" empty="Nothing new in the later run." />
        </div>
      </>
    )
  }

  return (
    <Page title="Compare findings" hint="Before = only in run A (removed), After = only in run B (added)." context={back}>
      {body}
    </Page>
  )
}
